import { useState, useEffect } from 'react';
import {
  Grid,
  Button,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
  DialogContent,
  DialogActions,
  FormHelperText,
  IconButton,
  Box,
  Dialog
} from '@mui/material';
import { toast } from 'react-toastify';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { API } from '../../../../api';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { popupCountActions } from '../../../../redux/slices/poupsCountsSlice';
import { getUserDetails } from '../../../../utils/orgName';
import useProfileDetails from '../../../../hooks/useProfileDetails';
import { userActions } from '../../../../redux/slices/user-profile/userProfieSlice';
import CommonModalHeader from '../../../../components/DialogUi/CommonModalHeader';

const LearningObjectivesNewModal = ({ open, onClose }: { open: boolean; onClose: any }) => {
  const { ftue, objectivesNumber } = useProfileDetails();
  const dispatch = useAppDispatch();
  const { orgId, userId } = getUserDetails();
  const selectedGroupId = useAppSelector((state) => state.selectedGroup.data);
  const { lastseen } = useProfileDetails();
  const isMentorLastSeen = lastseen?.role === 'mentor';

  const popuplength = useAppSelector((state) => state.popupsCount.popuplength);
  const count = useAppSelector((state) => state.popupsCount.count);
  const nextPopUpOrNot = popuplength - count;

  const getLocalStorageKey = (key: string) => `learningObjectives_${userId}_${key}`;
  const getInitialState = (key: string, defaultValue: any) => {
    const localStorageKey = getLocalStorageKey(key);
    const savedValue = localStorage.getItem(localStorageKey);
    return savedValue ? JSON.parse(savedValue) : defaultValue;
  };

  const [learningObjectiveCount, setLearningObjectiveCount] = useState(
    getInitialState('learningObjectiveCount', objectivesNumber || 3)
  );
  const [selectedObjectives, setSelectedObjectives] = useState<string[]>(getInitialState('selectedObjectives', []));
  const [displayedObjectivesCount, setDisplayedObjectivesCount] = useState(
    Math.max(getInitialState('displayedObjectivesCount', objectivesNumber || 3), selectedObjectives.length || objectivesNumber || 3)
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ftueLoading, setFtueLoading] = useState(false);
  const [objectives, setObjectives] = useState<string[]>([]);
  const [maxObjectives, setMaxObjectives] = useState(getInitialState('maxObjectives', 10));
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    localStorage.setItem(getLocalStorageKey('selectedObjectives'), JSON.stringify(selectedObjectives));
    localStorage.setItem(getLocalStorageKey('displayedObjectivesCount'), JSON.stringify(displayedObjectivesCount));
    localStorage.setItem(getLocalStorageKey('learningObjectiveCount'), JSON.stringify(learningObjectiveCount));
  }, [selectedObjectives, displayedObjectivesCount, learningObjectiveCount, userId]);

  useEffect(() => {
    const fetchLearningObjectives = async () => {
      try {
        const storedObjectives = localStorage.getItem(getLocalStorageKey('objectives'));
        const storedMax = localStorage.getItem(getLocalStorageKey('maxObjectives'));
        if (storedObjectives && storedMax) {
          setObjectives(JSON.parse(storedObjectives));
          setMaxObjectives(JSON.parse(storedMax));
          return;
        }

        const response = await API.getUserLearningObjectives({ orgId });
        const objectivesData = response?.data?.data ?? [];

        if (Array.isArray(objectivesData)) {
          const objectiveList = objectivesData.map((obj) => obj.objective);
          const calculatedMax = objectivesData.length > 0 ? objectivesData.length : 10;

          setObjectives(objectiveList);
          setMaxObjectives(calculatedMax);

          localStorage.setItem(getLocalStorageKey('objectives'), JSON.stringify(objectiveList));
          localStorage.setItem(getLocalStorageKey('maxObjectives'), JSON.stringify(calculatedMax));
        }
      } catch (error) {
        console.error('Error fetching learning objectives:', error);
        setObjectives([]);
      }
    };
    fetchLearningObjectives();
  }, [orgId]);

  useEffect(() => {
    if (objectivesNumber) {
      setLearningObjectiveCount(objectivesNumber);
      setDisplayedObjectivesCount(objectivesNumber);
    }
  }, [objectivesNumber]);

  const handleInputChange = (index: number, value: string) => {
    const updatedObjectives = [...selectedObjectives];
    while (updatedObjectives.length <= index) {
      updatedObjectives.push('');
    }
    updatedObjectives[index] = value;

    const cleanObjectives = updatedObjectives.filter((obj) => obj !== '');
    setSelectedObjectives(cleanObjectives);
  };

  const checkErrors = () => {
    const hasObjectives = selectedObjectives.some((obj) => obj && obj.trim() !== '');

    if (!hasObjectives) {
      return true;
    }
    return false;
  };

  const updateFtue = async () => {
    setFtueLoading(true);
    try {
      const response = await API.updateUserFtue({ ftue: false, userId });
      if (response.status === 200) {
        localStorage.removeItem(getLocalStorageKey('selectedObjectives'));
        localStorage.removeItem(getLocalStorageKey('learningObjectiveCount'));
        localStorage.removeItem(getLocalStorageKey('displayedObjectivesCount'));
        localStorage.removeItem(getLocalStorageKey('objectives'));
        dispatch(userActions.userFtueUpdate(0));
        setFtueLoading(false);
      }
    } catch (e) {
      toast.error('Your profile could not be saved. Try again.');
      setFtueLoading(false);
    }
  };

  const onSubmit = async () => {
    if (checkErrors()) {
      toast.error(`Select at least one ${isMentorLastSeen ? 'area of expertise' : 'learning objective'}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const objectivesToSave = selectedObjectives.filter((objective) => objective !== '');
      const savedObjectivesKey = getLocalStorageKey('savedObjectives');
      const savedObjectives = localStorage.getItem(savedObjectivesKey);
      const parsedSavedObjectives = savedObjectives ? JSON.parse(savedObjectives) : [];
      const areObjectivesSame =
        objectivesToSave.length === parsedSavedObjectives.length &&
        objectivesToSave.every((obj) => parsedSavedObjectives.includes(obj));

      if (areObjectivesSame) {
        setIsSubmitting(false);
        return;
      }
      const response = await API.getUserLearningObjectives({ orgId });
      const existingObjectives = response?.data?.data || [];

      const validObjectives = objectivesToSave.filter((objective) =>
        existingObjectives.some((existing: any) => existing.objective === objective)
      );

      const newlyAddedOnly = validObjectives.filter((o) => !parsedSavedObjectives.includes(o));
      const payload = {
        learningObjectives:
          newlyAddedOnly.length > 0
            ? existingObjectives
                .filter((existing: any) => newlyAddedOnly.includes(existing.objective))
                .map((existing: any) => ({ id: existing.id, objective: existing.objective }))
            : []
      };

      if (payload.learningObjectives.length > 0) {
        const saveResponse = await API.saveLearningObjectives(payload);
        if (saveResponse.status === 200) {
          const fullList = existingObjectives
            .filter((existing: any) => validObjectives.includes(existing.objective))
            .map((existing: any) => ({ id: existing.id, objective: existing.objective }));
          localStorage.setItem(savedObjectivesKey, JSON.stringify(validObjectives));
          localStorage.setItem(getLocalStorageKey('learningObjectiveCount'), JSON.stringify(learningObjectiveCount));
          dispatch(userActions.updateObjectives(fullList));
          const response = await API.updateMentorshipScreen();
          dispatch(userActions.userMentorshipScreen(true));
          onClose();
        }
      }
    } catch (error) {
      toast.error('Learning objectives could not be saved. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFilteredOptions = (index: number) => {
    const filteredOptions = objectives.filter(
      (option) => !selectedObjectives.includes(option) || selectedObjectives[index] === option
    );
    // if (
    //   inputValue &&
    //   inputValue.trim() !== '' &&
    //   !filteredOptions.includes(inputValue) &&
    //   !objectives.includes(inputValue)
    // ) {
    //   return [...filteredOptions, `Add "${inputValue}"`];
    // }
    return filteredOptions;
  };

  const handleOptionSelection = (index: number, newValue: string | null) => {
    setSelectedObjectives((prev) => {
      const updatedObjectives = [...prev];

      if (!newValue) {
        updatedObjectives[index] = '';
      } else if (newValue.startsWith('Add "') && newValue.endsWith('"')) {
        const customValue = newValue.slice(5, -1).trim();
        if (customValue !== '' && !objectives.includes(customValue)) {
          setObjectives((prevObjectives) => [...prevObjectives, customValue]);
          localStorage.setItem(getLocalStorageKey('objectives'), JSON.stringify([...objectives, customValue]));
        }
        updatedObjectives[index] = customValue;
      } else {
        updatedObjectives[index] = newValue;
      }

      return updatedObjectives;
    });

    setInputValue('');
  };
  useEffect(() => {
    if (selectedObjectives.length > displayedObjectivesCount) {
      setDisplayedObjectivesCount(selectedObjectives.length);
    }
  }, [selectedObjectives.length, displayedObjectivesCount]);

  const addObjectiveField = () => {
    if (displayedObjectivesCount < maxObjectives) {
      setDisplayedObjectivesCount((prev) => prev + 1);
      setLearningObjectiveCount((prev: any) => prev + 1);
      if (selectedObjectives.length < displayedObjectivesCount + 1) {
        setSelectedObjectives((prev) => [...prev, '']);
      }
    }
  };

  const getDialogActions = () => {
    return (
      <DialogActions>
        <Button color="primary" variant="contained" fullWidth disabled={isSubmitting || ftueLoading} onClick={onSubmit}>
          {isSubmitting || ftueLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    );
  };
  return (
    <Dialog open={open}>
      <CommonModalHeader
        title={isMentorLastSeen ? 'Areas of Expertise' : 'Learning Objectives'}
        handleCloseModal={() => {}}
      />
      <DialogContent>
        <Grid container spacing={2}>
          <Typography mt={'10px'} px={2} fontSize={'12px'} fontStyle={'italic'}>
            {isMentorLastSeen
              ? 'Select the areas in which you can help people in this program'
              : 'Your organization would like you to complete the following number of objectives from their list as part of your program'}
          </Typography>
          {Array.from({ length: displayedObjectivesCount }).map((_, index) => (
            <Grid item xs={12} key={index}>
              <InputLabel>{`${isMentorLastSeen ? 'Areas of expertise' : 'Learning Objective'} ${
                index + 1
              }`}</InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  size="small"
                  id={`learning-objective-${index}`}
                  value={selectedObjectives[index] || ''}
                  options={getFilteredOptions(index)}
                  onChange={(_, newValue) => handleOptionSelection(index, newValue)}
                  onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder={`Select ${isMentorLastSeen ? 'area of expertise' : 'learning Objective'} ${
                        index + 1
                      }`}
                      error={!!errors[index]}
                    />
                  )}
                />
                <FormHelperText error>{errors[index]}</FormHelperText>
              </FormControl>
            </Grid>
          ))}
        </Grid>

        {isMentorLastSeen && displayedObjectivesCount < maxObjectives && (
          <Box mt={2}>
            <IconButton size="small" onClick={addObjectiveField}>
              <AddCircleIcon fontSize="large" color="info" />
            </IconButton>
            <Typography variant="body2" component="span" sx={{ ml: 1 }}>
              Add another area of expertise
            </Typography>
          </Box>
        )}
      </DialogContent>

      {getDialogActions()}
    </Dialog>
  );
};

export default LearningObjectivesNewModal;
